import { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Badge,
  Progress,
  Table,
  Input,
  InputGroup,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Pagination,
  PaginationItem,
  PaginationLink,
  Spinner,
  Alert
} from 'reactstrap';
import { Icon } from '../../components/Component';
import Content from '../../layout/content/Content';
import Head from '../../layout/head/Head';
import { Bar } from 'react-chartjs-2';
import { defaultUserImage, getPublicDocumentUrl } from '../../utils/envConfig';
import { formatCurrency, formatMilliseconds } from '../../utils/Utils';
import { Link, useLocation } from 'react-router-dom';
import { Chart, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import LeadFunnel from './components/LeadFunnel';
import LeadSourcePerformance from './components/LeadSourcePerformance';
import { useQuery } from 'react-query';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { getAgentLeaderboard } from '../../api/agents/getAgentLeaderboard';
import { getTeamPerformance } from '../../api/agents/getTeamPerformance';
import { getAllHotLeadFunnel } from '../../api/agents/getAllHotLeadFunnel';
import DateRangePresetFilter from '../components/filter-components/DateRangePresetFilter';
import AgentTeamFilter from '../components/filter-components/AgentTeamFilter';
import getAllTeamLeaders from '../../api/agents/getAllTeamLeaders';
import ExportModal from '../../components/export/ExportModal';
import {
  exportAsImage,
  exportAsPDF,
  EXPORT_FORMATS,
  createExportContainer,
  PRESENTATION_THEMES,
  RANKING_OPTIONS
} from '../../utils/export/exportUtils';
import {
  generatePresentationTemplate,
  generateCompactPresentationTemplate
} from '../../utils/export/presentationTemplates';

// Styled component to override card hover animations
const NoHoverStyles = styled.div`
  .card {
    transition: none !important;
    transform: none !important;
  }

  .card:hover {
    transform: none !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important; /* Retain a subtle shadow for consistency if desired */
  }
`;

// Register Chart.js components
Chart.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, Filler);

// Default lead funnel data
const defaultLeadFunnelData = {
  leadAssigned: 0,
  followUp: 0,
  prospect: 0,
  listingOrDeal: 0,
  won: 0
};

// Function to generate team performance data from API response
const generateTeamPerformanceData = (teamsData) => {
  // Default data when API data is not available
  const defaultData = {
    labels: ['Deals Completed', 'Listings Acquired', 'Lead Conversions', 'Activities'],
    datasets: []
  };

  // If we have real data, use it
  if (teamsData && teamsData.length > 0) {
    // Map the API data to the chart format
    const datasets = teamsData.map((team, index) => {
      // Use a set of predefined colors for teams
      const colors = ['#4361ee', '#f72585', '#4cc9f0', '#7209b7', '#3a0ca3', '#2dce89'];

      // Get team leader name or use placeholder
      const teamLeaderName = team.teamLeader ?
        `${team.teamLeader.first_name} ${team.teamLeader.last_name}` :
        `Team ${index + 1}`;

      return {
        label: teamLeaderName,
        data: [
          team.dealsCompleted || 0,
          team.listingsAcquired || 0,
          team.firstActionCount || 0,
          team.activitiesCompleted || 0
        ],
        backgroundColor: colors[index % colors.length],
      };
    });

    return {
      ...defaultData,
      datasets
    };
  }

  return defaultData;
};

// Chart options
const barChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
      labels: {
        boxWidth: 10,
        usePointStyle: true,
        pointStyle: 'circle'
      }
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      padding: 10,
      titleFont: {
        size: 14
      },
      bodyFont: {
        size: 13
      },
      callbacks: {
        label: function (context) {
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          if (context.parsed.y !== null) {
            label += context.parsed.y;
          }
          return label;
        },
        title: function (context) {
          // Get the category name based on the index
          const categories = ['Deals Completed', 'Listings Acquired', 'Lead Conversions', 'Activities'];
          return categories[context[0].dataIndex];
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      grid: {
        color: 'rgba(219, 223, 234, 0.2)',
        drawBorder: false
      },
      ticks: {
        font: {
          size: 12
        },
        color: '#8094ae',
        padding: 8
      }
    },
    x: {
      grid: {
        display: false,
        drawBorder: false
      },
      ticks: {
        font: {
          size: 12
        },
        color: '#8094ae',
        padding: 5
      }
    }
  }
};

const AgentLeaderboard = () => {
  const axios = useAxiosPrivate();
  const location = useLocation();

  // Default filter state
  const defaultFilters = useMemo(() => ({
    period: 'monthly',
    searchTerm: '',
    sortBy: 'totalPoints',
    sortDirection: 'desc',
    currentPage: 1,
    itemsPerPage: 15,
    startDate: null,
    endDate: null,
    teamId: null
  }), []);

  // Helper functions for URL parameter encoding/decoding
  const decodeBase64 = useCallback((str) => {
    try {
      return JSON.parse(atob(str));
    } catch (e) {
      return defaultFilters;
    }
  }, [defaultFilters]);

  const encodeBase64 = useCallback((obj) => {
    return btoa(JSON.stringify(obj));
  }, []);

  // Initialize state from URL or defaults
  const getInitialFilters = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const base64Query = searchParams.get('q');
    if (base64Query) {
      const parsedFilters = decodeBase64(base64Query);
      return {
        ...defaultFilters,
        ...parsedFilters,
        // Convert date strings back to Date objects
        startDate: parsedFilters.startDate ? new Date(parsedFilters.startDate) : null,
        endDate: parsedFilters.endDate ? new Date(parsedFilters.endDate) : null
      };
    }
    return defaultFilters;
  }, [location.search, decodeBase64, defaultFilters]);

  const initialFilters = getInitialFilters();

  const [period, setPeriod] = useState(initialFilters.period);
  const [filterDropdown, setFilterDropdown] = useState(false); // This state doesn't seem to be used
  const [searchTerm, setSearchTerm] = useState(initialFilters.searchTerm);
  const [sortBy, setSortBy] = useState(initialFilters.sortBy);
  const [sortDirection, setSortDirection] = useState(initialFilters.sortDirection);
  const [currentPage, setCurrentPage] = useState(initialFilters.currentPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialFilters.itemsPerPage);
  const [pageSizeDropdown, setPageSizeDropdown] = useState(false);
  const [startDate, setStartDate] = useState(initialFilters.startDate);
  const [endDate, setEndDate] = useState(initialFilters.endDate);
  const [teamId, setTeamId] = useState(initialFilters.teamId);

  // Export modal state
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  // Fetch team leaders for the team filter
  const {
    data: teamLeadersData,
    isLoading: isLoadingTeamLeaders
  } = useQuery(
    ['teamLeaders'],
    () => getAllTeamLeaders(axios),
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  // Get the selected team option for display
  const selectedTeamOption = useMemo(() => {
    if (!teamId || !teamLeadersData?.data) return null;

    const selectedTeam = teamLeadersData.data.find(leader => leader?.userId === teamId);
    if (selectedTeam) {
      return {
        value: selectedTeam.userId,
        label: `Team ${selectedTeam.user?.first_name}`,
        ...selectedTeam
      };
    }
    return null;
  }, [teamId, teamLeadersData]);

  // Prepare query parameters for agent leaderboard
  const queryParams = {
    period,
    limit: itemsPerPage,
    skip: (currentPage - 1) * itemsPerPage,
    sortBy, // Include sortBy for backend sorting
    sortDirection, // Include sortDirection for backend sorting
    ...(startDate && { startDate: startDate.toISOString() }),
    ...(endDate && { endDate: endDate.toISOString() }),
    ...(teamId && { teamId }),
  };

  // Fetch agent leaderboard data using React Query
  const {
    data: leaderboardData,
    isLoading: isLoadingLeaderboard,
    isError: isErrorLeaderboard
  } = useQuery(
    ['agentLeaderboard', JSON.stringify(queryParams)], // Query key includes all params that trigger refetch
    () => getAgentLeaderboard(axios, { params: queryParams }),
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      keepPreviousData: true,
    }
  );

  // Fetch team performance data using React Query
  const {
    data: teamData,
    isLoading: isLoadingTeamData,
    isError: isErrorTeamData
  } = useQuery(
    ['teamPerformance', period, startDate, endDate],
    () => getTeamPerformance(axios, {
      params: {
        period,
        limit: 10, // Limit to top 10 teams
        skip: 0,
        ...(startDate && { startDate: startDate.toISOString() }),
        ...(endDate && { endDate: endDate.toISOString() })
      }
    }),
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  // Fetch hot lead funnel data using React Query
  const {
    data: hotLeadFunnelData,
    isLoading: isLoadingHotLeadFunnel,
    isError: isErrorHotLeadFunnel
  } = useQuery(
    ['allHotLeadFunnel', period, startDate, endDate, teamId],
    () => getAllHotLeadFunnel(axios, {
      params: {
        period,
        ...(startDate && { startDate: startDate.toISOString() }),
        ...(endDate && { endDate: endDate.toISOString() }),
        ...(teamId && { teamId })
      }
    }),
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  // Generate team performance chart data
  const teamPerformanceChartData = generateTeamPerformanceData(teamData?.data || []);

  // Get lead funnel data from API or fallback to aggregated team data
  const leadFunnelData = useMemo(() => {
    if (hotLeadFunnelData && !hotLeadFunnelData.error) {
      return hotLeadFunnelData;
    }
    if (!teamData?.data || teamData.data.length === 0) {
      return defaultLeadFunnelData;
    }
    const aggregatedData = teamData.data.reduce((acc, team) => {
      if (team.leadFunnel) {
        return {
          leadAssigned: (acc.leadAssigned || 0) + (team.leadFunnel.leadAssigned || 0),
          followUp: (acc.followUp || 0) + (team.leadFunnel.followUp || 0),
          prospect: (acc.prospect || 0) + (team.leadFunnel.prospect || 0),
          listingOrDeal: (acc.listingOrDeal || 0) + (team.leadFunnel.listingOrDeal || 0),
          won: (acc.won || 0) + (team.leadFunnel.won || 0)
        };
      }
      return acc;
    }, { ...defaultLeadFunnelData });
    return aggregatedData;
  }, [hotLeadFunnelData, teamData]);

  // Update URL when filters change
  useEffect(() => {
    const currentFilters = {
      period,
      searchTerm,
      sortBy,
      sortDirection,
      currentPage,
      itemsPerPage,
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
      teamId
    };

    // Remove null/undefined values to keep URL clean
    const cleanFilters = Object.fromEntries(
      Object.entries(currentFilters).filter(([_, value]) => value !== null && value !== undefined && value !== '')
    );

    const encodedFilters = encodeBase64(cleanFilters);

    // Update URL without triggering a page reload
    window.history.pushState(
      null,
      "",
      `${location.pathname}?q=${encodedFilters}`
    );
  }, [period, searchTerm, sortBy, sortDirection, currentPage, itemsPerPage, startDate, endDate, teamId, location.pathname, encodeBase64]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [period, startDate, endDate, teamId, itemsPerPage, sortBy, sortDirection]); // Added sortBy, sortDirection

  // Filter agents by search term (client-side filtering after data fetch)
  // If sorting/filtering is primarily backend, this might only be for display refinement or if backend doesn't support search
  const filteredAgents = useMemo(() => {
    if (!leaderboardData?.data) return [];
    let agents = leaderboardData.data;

    if (searchTerm) {
      agents = agents.filter(item => {
        const agentName = `${item.agent?.user?.firstName || ''} ${item.agent?.user?.lastName || ''}`.toLowerCase();
        const email = (item.agent?.user?.email || '').toLowerCase();
        const search = searchTerm.toLowerCase();
        return agentName.includes(search) || email.includes(search);
      });
    }
    // Client-side sorting (can be removed if backend handles all sorting)
    // For simplicity, assuming backend handles sorting based on sortBy, sortDirection passed in queryParams.
    // If client-side sorting is still needed after fetch:
    // agents.sort((a, b) => { ... });
    return agents;
  }, [leaderboardData, searchTerm /*, sortBy, sortDirection */]);


  const totalCount = leaderboardData?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc'); // Default to ascending when changing sort field
    }
    // setCurrentPage(1); // Reset to page 1 on sort, handled by useEffect
  };

  const handlePageSizeChange = (size) => {
    setItemsPerPage(size);
    // setCurrentPage(1); // Reset to page 1, handled by useEffect
  };

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setTeamId(null);
    setSearchTerm('');
    setPeriod('monthly');
    setSortBy('totalPoints'); // Reset sort
    setSortDirection('desc'); // Reset sort direction
    // setCurrentPage(1); // Handled by useEffect
  };

  // Export functionality
  const toggleExportModal = () => {
    setIsExportModalOpen(!isExportModalOpen);
    setExportError(null);
  };

  const handleExport = async (config) => {
    setIsExporting(true);
    setExportError(null);

    try {
      // Create a temporary container for the presentation
      const { container, cleanup } = createExportContainer(
        document.querySelector('.modern-dashboard'),
        PRESENTATION_THEMES[config.theme]
      );

      // Generate presentation HTML using appropriate template
      const presentationHTML = config.presentationStyle === 'compact'
        ? generateCompactPresentationTemplate(config, filteredAgents, startDate, endDate)
        : generatePresentationTemplate(config, filteredAgents, startDate, endDate);

      container.innerHTML = presentationHTML;

      // Wait for rendering to complete
      await new Promise(resolve => setTimeout(resolve, 500));

      // Export based on format
      let result;
      if (config.format === EXPORT_FORMATS.PDF) {
        result = await exportAsPDF(container, config.filename, {
          orientation: config.orientation,
          canvasOptions: { scale: 2, backgroundColor: '#ffffff' }
        });
      } else {
        result = await exportAsImage(container, config.filename, config.format, {
          canvasOptions: { scale: 2, backgroundColor: '#ffffff' }
        });
      }

      // Cleanup
      cleanup();

      if (result.success) {
        setIsExportModalOpen(false);
      } else {
        setExportError(result.message);
      }
    } catch (error) {
      console.error('Export error:', error);
      setExportError('Failed to generate export. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Head title="Agent Leaderboard" />
      <Content>
        <NoHoverStyles >
          <div className="modern-dashboard" style={{ minHeight: 'calc(100vh - 120px)' }}>
            {/* Header */}
            <div className="dashboard-header mb-4">
              <Row className="align-items-center">
                <Col lg="6" md="12">
                  <h2 className="mb-2">Agent Leaderboard</h2>
                  <p className="text-muted">Track and compare agent performance metrics</p>
                </Col>
                <Col lg="6" md="12">
                  <div className="d-flex flex-wrap justify-content-start justify-content-lg-end mt-3 mt-lg-0 gap-2">
                    <div className="d-flex gap-2 flex-wrap">
                      <DateRangePresetFilter
                        startDate={startDate}
                        endDate={endDate}
                        period={period}
                        onDateRangeChange={(start, end) => {
                          setStartDate(start);
                          setEndDate(end);
                        }}
                        onPeriodChange={(newPeriod) => setPeriod(newPeriod)}
                      />
                      <div style={{ width: '200px' }}>
                        <AgentTeamFilter
                          stateVar={selectedTeamOption}
                          onChange={(option) => setTeamId(option ? option.value : null)}
                          placeholder="Filter by Team"
                        />
                      </div>
                      <Button color="light" onClick={clearFilters}>
                        <Icon name="filter-off"></Icon> Clear Filters
                      </Button>
                      <Button color="primary" onClick={toggleExportModal}>
                        <Icon name="download"></Icon> Export Report
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            {/* Top Performers */}
            <h4 className="mb-3">Top Performers</h4>
            <Row className="g-3 mb-4">
              {/* Assuming filteredAgents are already sorted by backend; taking top 3 from current page if not globally sorted for this section */}
              {/* Or, if this section must always show absolute top 3, a separate API call or processing might be needed */}
              {isLoadingLeaderboard && !leaderboardData ? (
                [...Array(3)].map((_, index) => ( // Skeleton loaders for top performers
                  <Col lg="4" md="6" sm="12" key={`skeleton-${index}`}>
                    <Card className="h-100 border-0" style={{ transition: 'none' }}>
                      <CardBody className="p-3 p-md-4">
                        <div className="d-flex align-items-center mb-3">
                          <div className="skeleton-rank me-3" style={{ width: '40px', height: '40px', backgroundColor: '#e0e0e0', borderRadius: '50%' }}></div>
                          <div className="skeleton-avatar me-3" style={{ width: '50px', height: '50px', backgroundColor: '#e0e0e0', borderRadius: '50%' }}></div>
                          <div>
                            <div style={{ width: '120px', height: '20px', backgroundColor: '#e0e0e0', marginBottom: '5px' }}></div>
                            <div style={{ width: '150px', height: '15px', backgroundColor: '#e0e0e0' }}></div>
                          </div>
                        </div>
                        <div style={{ width: '100%', height: '10px', backgroundColor: '#e0e0e0', marginBottom: '15px' }}></div>
                        <div className="d-flex justify-content-between text-muted small">
                          <div style={{ width: '60px', height: '15px', backgroundColor: '#e0e0e0' }}></div>
                          <div style={{ width: '70px', height: '15px', backgroundColor: '#e0e0e0' }}></div>
                          <div style={{ width: '50px', height: '15px', backgroundColor: '#e0e0e0' }}></div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))
              ) : filteredAgents.slice(0, 3).map((agent, index) => (
                <Col lg="4" md="6" sm="12" key={agent.id || index}>
                  <Card className="h-100 border-0" style={{ transition: 'none' }}>
                    <CardBody className="p-3 p-md-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className={`rank-badge rank-${index + 1} me-3`}>#{agent.rank || index + 1}</div> {/* Use agent.rank if available */}
                        <div className="user-avatar md me-3">
                          <img
                            src={agent.agent?.user?.photo ? getPublicDocumentUrl(agent.agent?.user?.photo) : defaultUserImage}
                            alt={`${agent.agent?.user?.firstName || ''} ${agent.agent?.user?.lastName || ''}`}
                            className="rounded-circle"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </div>
                        <div>
                          <h5 className="mb-0">{`${agent.agent?.user?.firstName || ''} ${agent.agent?.user?.lastName || ''}`}</h5>
                          <small className="text-muted">{agent.agent?.user?.email || ''}</small>
                        </div>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Overall Score</span>
                        <span className="fw-bold">{agent.totalPoints || 0} points</span>
                      </div>
                      <Progress value={((agent.totalPoints || 0) / (leaderboardData?.maxPossiblePoints || 300)) * 100} className="mb-3" /> {/* Use dynamic max points if available */}
                      <div className="d-flex justify-content-between text-muted small">
                        <span>Deals: {agent.dealsCompleted || 0}</span>
                        <span>Listings: {agent.listingsAcquired || 0}</span>
                        <span>Leads: {agent.firstActionCount || 0}</span>
                      </div>
                      <div className="mt-3 text-end">
                        <Link to={`/agent-detail/${agent.agentId || agent.id}`} className="btn btn-sm btn-primary"> {/* Ensure correct ID path */}
                          View Details
                        </Link>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Two-column layout: Team Performance on left, Leaderboard on right */}
            <Row className="g-4 mb-4" style={{ minHeight: '850px' }}>
              {/* Left Column - Team Performance */}
              <Col xl="5" lg="12" className="d-flex">

                <div className="d-flex flex-column gap-4 w-100" style={{ minHeight: '100%' }}>
                  <Card className="shadow-sm border-0" style={{ transition: 'none' }}>
                    <CardBody className="p-3 p-md-4">
                      <h5 className="card-title mb-4">Team Performance Comparison</h5>
                      {isLoadingTeamData ? (
                        <div className="d-flex justify-content-center my-5">
                          <Spinner color="primary" />
                        </div>
                      ) : isErrorTeamData ? (
                        <Alert color="danger">
                          Failed to load team performance data. Please try again.
                        </Alert>
                      ) : teamData?.data?.length === 0 ? (
                        <div className="text-center py-5">
                          <p className="text-muted">No team performance data available for the selected period.</p>
                        </div>
                      ) : (
                        <div style={{ height: '400px' }}>
                          <Bar data={teamPerformanceChartData} options={barChartOptions} />
                        </div>
                      )}
                    </CardBody>
                  </Card>

                  <div className="flex-grow-1 d-flex flex-column"> {/* Make this container grow to fill remaining space */}
                    {isLoadingTeamData || isLoadingHotLeadFunnel ? ( // Combined loading state for funnel
                      <Card className="shadow-sm border-0 flex-grow-1 d-flex flex-column" style={{ transition: 'none' }}>
                        <CardBody className="p-3 p-md-4 d-flex flex-column justify-content-center">
                          <h5 className="card-title mb-4">Hot Lead Conversion Funnel</h5>
                          <div className="d-flex justify-content-center my-5">
                            <Spinner color="primary" />
                          </div>
                        </CardBody>
                      </Card>
                    ) : isErrorTeamData || isErrorHotLeadFunnel ? ( // Combined error state
                      <Card className="shadow-sm border-0 flex-grow-1 d-flex flex-column" style={{ transition: 'none' }}>
                        <CardBody className="p-3 p-md-4 d-flex flex-column justify-content-center">
                          <h5 className="card-title mb-4">Hot Lead Conversion Funnel</h5>
                          <Alert color="danger">
                            Failed to load lead funnel data. Please try again.
                          </Alert>
                        </CardBody>
                      </Card>
                    ) : (
                      <div className="flex-grow-1">
                        <LeadFunnel
                          data={leadFunnelData}
                          isHotLeadData={!!hotLeadFunnelData && !hotLeadFunnelData.error}
                          showPercentages={true}
                          title="Hot Lead Conversion Funnel"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Col>

              {/* Right Column - Agent Rankings */}
              <Col xl="7" lg="12">
                <Card className="shadow-sm border-0 " style={{ transition: 'none', minHeight: '1050px' }}>
                  <CardBody className="p-3 p-md-4">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
                      <h5 className="card-title mb-0">Leaderboard</h5>
                      <div className="d-flex justify-content-end">
                        <InputGroup style={{ maxWidth: '250px' }}>
                          <Input
                            placeholder="Search agents..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <Button color="primary" onClick={() => {/* if search is backend, trigger refetch or set a search query param */ }}>
                            <Icon name="search"></Icon>
                          </Button>
                        </InputGroup>
                      </div>
                    </div>
                    {isLoadingLeaderboard ? (
                      <div className="d-flex justify-content-center my-5">
                        <Spinner color="primary" />
                      </div>
                    ) : isErrorLeaderboard ? (
                      <Alert color="danger">
                        Failed to load leaderboard data. Please try again.
                      </Alert>
                    ) : (
                      <>
                        {/* Desktop View - Full Table with responsive design */}
                        <div className="d-none d-lg-block table-responsive rounded rounded-lg"  >
                          <Table className="" responsive hover style={{ borderRadius: "20px" }} > {/* Added table-striped and hover */}
                            <thead>
                              <tr style={{ backgroundColor: '#f8f9fa', border: '2px solid #e9ecef' }}>
                                <th style={{ padding: '12px 8px', width: '60px', textAlign: 'center', verticalAlign: "middle" }}>Rank</th>
                                <th style={{ padding: '12px 8px', minWidth: '180px', textAlign: 'center', verticalAlign: "middle" }}>Agent</th>
                                <th onClick={() => handleSort('dealsCompleted')} style={{ cursor: 'pointer', padding: '12px 8px', width: '80px', textAlign: 'center', verticalAlign: "middle" }}>
                                  Deals {sortBy === 'dealsCompleted' && <Icon name={sortDirection === 'asc' ? 'arrow-up' : 'arrow-down'} />}
                                </th>
                                <th onClick={() => handleSort('dealValue')} style={{ cursor: 'pointer', padding: '12px 8px', width: '120px', textAlign: 'center', verticalAlign: "middle" }}>
                                  Value {sortBy === 'dealValue' && <Icon name={sortDirection === 'asc' ? 'arrow-up' : 'arrow-down'} />}
                                </th>
                                <th onClick={() => handleSort('commissionValue')} style={{ cursor: 'pointer', padding: '12px 8px', width: '120px', textAlign: 'center', verticalAlign: "middle" }}>
                                  Comm. {sortBy === 'commissionValue' && <Icon name={sortDirection === 'asc' ? 'arrow-up' : 'arrow-down'} />}
                                </th>
                                <th onClick={() => handleSort('listingsAcquired')} style={{ cursor: 'pointer', padding: '12px 8px', width: '80px', textAlign: 'center', verticalAlign: "middle" }}>
                                  List {sortBy === 'listingsAcquired' && <Icon name={sortDirection === 'asc' ? 'arrow-up' : 'arrow-down'} />}
                                </th>
                                <th onClick={() => handleSort('totalPoints')} style={{ cursor: 'pointer', padding: '12px 8px', width: '120px', textAlign: 'center', verticalAlign: "middle" }}>
                                  Score {sortBy === 'totalPoints' && <Icon name={sortDirection === 'asc' ? 'arrow-up' : 'arrow-down'} />}
                                </th>
                                <th onClick={() => handleSort('avgTimeToFirstAction')} style={{ cursor: 'pointer', padding: '12px 8px', width: '120px', textAlign: 'center', verticalAlign: "middle" }}>
                                  Act. Time {sortBy === 'avgTimeToFirstAction' && <Icon name={sortDirection === 'asc' ? 'arrow-up' : 'arrow-down'} />}
                                </th>
                                <th style={{ padding: '12px 8px', width: '100px', textAlign: 'right', verticalAlign: "middle" }}>Action</th>
                              </tr>
                            </thead>
                            <tbody style={{ minHeight: '850px' }}>
                              {filteredAgents.length > 0 ? (
                                filteredAgents.map((agent, index) => (
                                  <tr key={agent.id || index} style={{ border: '2px solid #f0f0f0' }}> {/* Kept explicit border for striped clarity */}
                                    <td style={{ backgroundColor: "#ffffff", padding: '12px 8px', textAlign: 'center', verticalAlign: 'middle' }}>
                                      <Badge color={agent.rank <= 3 ? 'primary' : 'light'} pill> {/* Use agent.rank for badge color if available */}
                                        #{agent.rank || ((currentPage - 1) * itemsPerPage + index + 1)}
                                      </Badge>
                                    </td>
                                    <td style={{ backgroundColor: "#ffffff", padding: '12px 8px', verticalAlign: 'middle' }}>
                                      <div className="d-flex align-items-center ms-4">
                                        <div className="user-avatar xs me-2 self-start">
                                          <img
                                            src={agent.agent?.user?.photo ? getPublicDocumentUrl(agent.agent?.user?.photo) : defaultUserImage}
                                            alt={`${agent.agent?.user?.firstName || ''} ${agent.agent?.user?.lastName || ''}`}
                                            className="rounded-circle"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                          />
                                        </div>
                                        {/* <span className="text-truncate " style={{ display: 'inline-block', maxWidth: '160px' }}>{`${agent.agent?.user?.firstName || ''} ${agent.agent?.user?.lastName || ''}`}</span> */}
                                        <span className="text-truncate " style={{ display: 'inline-block', maxWidth: '160px' }}>{`${agent.agent?.user?.firstName || ''}`}</span>
                                      </div>
                                    </td>
                                    <td style={{ backgroundColor: "#ffffff", padding: '12px 8px', textAlign: 'center', verticalAlign: 'middle' }}>{agent.dealsCompleted || 0}</td>
                                    <td style={{ backgroundColor: "#ffffff", padding: '12px 8px', textAlign: 'center', verticalAlign: 'middle' }}>{formatCurrency(agent.dealValue || 0)}</td>
                                    <td style={{ backgroundColor: "#ffffff", padding: '12px 8px', textAlign: 'center', verticalAlign: 'middle' }}>{formatCurrency(agent.commissionValue || 0)}</td>
                                    <td style={{ backgroundColor: "#ffffff", padding: '12px 8px', textAlign: 'center', verticalAlign: 'middle' }}>{agent.listingsAcquired || 0}</td>
                                    <td style={{ backgroundColor: "#ffffff", padding: '12px 8px', textAlign: 'center', verticalAlign: 'middle' }}>
                                      <div className="d-flex flex-column align-items-center justify-content-center">
                                        <span>{agent.totalPoints || 0}</span>
                                        <div className="w-50" style={{ minWidth: '50px' }}>
                                          <Progress value={((agent.totalPoints || 0) / (leaderboardData?.maxPossiblePoints || 300)) * 100} className="progress-sm" />
                                        </div>
                                      </div>
                                    </td>
                                    <td style={{ backgroundColor: "#ffffff", padding: '12px 8px', textAlign: 'left', verticalAlign: 'middle' }}> {/* Left align for better readability of formatted time */}
                                      {formatMilliseconds(agent.avgTimeToFirstAction || 0, ['hours', 'minutes'], { trimDays: true, fullWords: false })}
                                    </td>
                                    <td style={{ backgroundColor: "#ffffff", padding: '12px 8px', textAlign: 'center', verticalAlign: 'middle' }}>
                                      <Link to={`/agent-detail/${agent.agentId || agent.id}`} className="btn btn-sm btn-outline-primary"> {/* Changed to outline */}
                                        Details
                                      </Link>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="9" className="text-center py-4">
                                    No agents found matching your criteria.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>

                        {/* Mobile/Tablet View - Card Layout */}
                        <div className="d-block d-lg-none">
                          {filteredAgents.length > 0 ? (
                            <div className="agent-cards">
                              {filteredAgents.map((agent, index) => (
                                <Card key={agent.id || index} className="mb-3 border-0 shadow-sm">
                                  <CardBody className="p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                      <div className="d-flex align-items-center">
                                        <Badge color={agent.rank <= 3 ? 'primary' : 'light'} pill className="me-2">
                                          #{agent.rank || ((currentPage - 1) * itemsPerPage + index + 1)}
                                        </Badge>
                                        <div className="user-avatar xs me-2">
                                          <img
                                            src={agent.agent?.user?.photo ? getPublicDocumentUrl(agent.agent?.user?.photo) : defaultUserImage}
                                            alt={`${agent.agent?.user?.firstName || ''} ${agent.agent?.user?.lastName || ''}`}
                                            className="rounded-circle"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                          />
                                        </div>
                                        <span className="fw-bold text-truncate" style={{ display: 'inline-block', maxWidth: '150px' }}>
                                          {`${agent.agent?.user?.firstName || ''} ${agent.agent?.user?.lastName || ''}`}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="badge bg-info text-dark rounded-pill"> {/* Changed badge color */}
                                          {agent.totalPoints || 0} pts
                                        </span>
                                      </div>
                                    </div>

                                    <Row className="g-2 mb-3 text-center">
                                      <Col xs="4">
                                        <small className="text-muted d-block">Deals</small>
                                        <span className="fw-medium">{agent.dealsCompleted || 0}</span>
                                      </Col>
                                      <Col xs="4">
                                        <small className="text-muted d-block">Listings</small>
                                        <span className="fw-medium">{agent.listingsAcquired || 0}</span>
                                      </Col>
                                      <Col xs="4">
                                        <small className="text-muted d-block">Resp. Time</small>
                                        <span className="fw-medium text-truncate d-block" style={{ fontSize: '0.8rem' }}>
                                          {formatMilliseconds(agent.avgTimeToFirstAction || 0, ['h', 'm'], { trimDays: true, fullWords: false })}
                                        </span>
                                      </Col>
                                      <Col xs="6" className="mt-2">
                                        <small className="text-muted d-block">Deal Value</small>
                                        <span className="fw-medium text-truncate d-block">{formatCurrency(agent.dealValue || 0)}</span>
                                      </Col>
                                      <Col xs="6" className="mt-2">
                                        <small className="text-muted d-block">Commission</small>
                                        <span className="fw-medium text-truncate d-block">{formatCurrency(agent.commissionValue || 0)}</span>
                                      </Col>
                                    </Row>

                                    <div className="d-flex justify-content-end align-items-center">
                                      <Link to={`/agent-detail/${agent.agentId || agent.id}`} className="btn btn-sm btn-primary">
                                        Details
                                      </Link>
                                    </div>
                                  </CardBody>
                                </Card>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4">
                              No agents found matching your criteria.
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Pagination */}
                    {totalPages > 0 && (
                      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center mt-4 gap-3">
                        <div>
                          <span className="text-muted me-2">Show</span>
                          <Dropdown isOpen={pageSizeDropdown} toggle={() => setPageSizeDropdown(!pageSizeDropdown)} className="d-inline-block">
                            <DropdownToggle color="outline-secondary" size="sm" className="btn-sm">
                              {itemsPerPage} <Icon name="chevron-down" />
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem onClick={() => handlePageSizeChange(10)}>10</DropdownItem>
                              <DropdownItem onClick={() => handlePageSizeChange(15)}>15</DropdownItem>
                              <DropdownItem onClick={() => handlePageSizeChange(20)}>20</DropdownItem>
                              <DropdownItem onClick={() => handlePageSizeChange(50)}>50</DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                          <span className="text-muted ms-2">entries</span>
                        </div>

                        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2">
                          <span className="text-muted d-none d-md-block me-3">
                            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries
                          </span>
                          <Pagination className="pagination-sm mb-0">
                            <PaginationItem disabled={currentPage === 1}>
                              <PaginationLink previous onClick={() => paginate(currentPage - 1)} />
                            </PaginationItem>

                            {/* Simplified Pagination Logic */}
                            {[...Array(totalPages)].map((_, i) => {
                              const pageNum = i + 1;
                              const showMaxPages = 5; // Max number of page links to show
                              const halfMaxPages = Math.floor(showMaxPages / 2);

                              if (totalPages <= showMaxPages) { // Show all pages if total is small
                                return (
                                  <PaginationItem key={i} active={pageNum === currentPage} className="d-none d-md-block">
                                    <PaginationLink onClick={() => paginate(pageNum)}>{pageNum}</PaginationLink>
                                  </PaginationItem>
                                );
                              }
                              // Logic for ellipsis
                              if (pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - halfMaxPages && pageNum <= currentPage + halfMaxPages)) {
                                return (
                                  <PaginationItem key={i} active={pageNum === currentPage} className="d-none d-md-block">
                                    <PaginationLink onClick={() => paginate(pageNum)}>{pageNum}</PaginationLink>
                                  </PaginationItem>
                                );
                              }
                              if (pageNum === currentPage - halfMaxPages - 1 || pageNum === currentPage + halfMaxPages + 1) {
                                return (<PaginationItem key={i} disabled className="d-none d-md-block"><PaginationLink>...</PaginationLink></PaginationItem>);
                              }
                              return null;
                            })}
                            {/* Always show current page for mobile */}
                            <PaginationItem active className="d-md-none">
                              <PaginationLink onClick={() => paginate(currentPage)}>{currentPage}</PaginationLink>
                            </PaginationItem>


                            <PaginationItem disabled={currentPage === totalPages}>
                              <PaginationLink next onClick={() => paginate(currentPage + 1)} />
                            </PaginationItem>
                          </Pagination>
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row >
              {/* Lead Source Performance */}
              <Col>
                <div className="mb-0"> {/* Changed from mb-4 to mb-0 if it's the absolute last element */}
                  {isLoadingHotLeadFunnel ? (
                    <Card className="shadow-sm border-0" style={{ transition: 'none' }}>
                      <CardBody className="p-3 p-md-4">
                        <h5 className="card-title mb-4">Lead Source Performance</h5>
                        <div className="d-flex justify-content-center my-5">
                          <Spinner color="primary" />
                        </div>
                      </CardBody>
                    </Card>
                  ) : isErrorHotLeadFunnel ? (
                    <Card className="shadow-sm border-0" style={{ transition: 'none' }}>
                      <CardBody className="p-3 p-md-4">
                        <h5 className="card-title mb-4">Lead Source Performance</h5>
                        <Alert color="danger">
                          Failed to load lead source data. Please try again.
                        </Alert>
                      </CardBody>
                    </Card>
                  ) : !hotLeadFunnelData?.hotLeadSourceMetrics || Object.keys(hotLeadFunnelData.hotLeadSourceMetrics).length === 0 ? (
                    <Card className="shadow-sm border-0" style={{ transition: 'none' }}>
                      <CardBody className="p-3 p-md-4">
                        <h5 className="card-title mb-4">Lead Source Performance</h5>
                        <div className="text-center py-4">
                          <p className="text-muted">No lead source data available for the selected period.</p>
                        </div>
                      </CardBody>
                    </Card>
                  ) : (
                    <LeadSourcePerformance
                      data={hotLeadFunnelData?.hotLeadSourceMetrics || {}}
                      title="Lead Source Performance"
                    />
                  )}
                </div>
              </Col>
            </Row>

          </div>
        </NoHoverStyles>
      </Content >

      {/* Export Modal */}
      <ExportModal
        isOpen={isExportModalOpen}
        toggle={toggleExportModal}
        onExport={handleExport}
        isLoading={isExporting}
        error={exportError}
        agentData={filteredAgents}
        currentRanking={Object.keys(RANKING_OPTIONS).find(key =>
          RANKING_OPTIONS[key].field === sortBy
        ) || 'TOTAL_POINTS'}
      />
    </>
  );
};

export default AgentLeaderboard;
